// Mob loot rolling (MOB-4; §3.3 loot). PURE (ENG-1).
//
// Rolls a loot table with the seeded Rng: weighted entry pick per pool roll,
// conditions (killed_by_player, random_chance), and functions (set_count min/max,
// looting_add). Distinct from block drops.ts (which is deterministic-min) because
// mob drops are random within documented bounds.
import type { Registries } from '../registry/load';
import type { LootTable } from '../registry/schemas';
import type { Rng } from '../engine/rng';
import { type ItemStack, makeStack } from '../game/inventory';

export interface LootOpts {
  /** The kill was dealt by a player (some drops gate on this). */
  killedByPlayer?: boolean;
  /** Looting enchant level (adds a bounded bonus). */
  looting?: number;
  /**
   * Host seam for the `explorer_map` loot function (M18): mint a filled map
   * pinned to the nearest `destination` structure and hand back the stack that
   * replaces the rolled entry. Pure loot rolling can't locate structures or own
   * map ids, so the host supplies this; absent (or returning null — flat/atlas
   * worlds, no such structure in range) leaves the plain rolled item in place.
   */
  explorerMap?(destination: string): ItemStack | null;
}

interface LootEntry {
  type: string;
  name?: string;
  weight?: number;
  functions?: Array<Record<string, unknown>>;
  conditions?: Array<Record<string, unknown>>;
}

function conditionsPass(conditions: Array<Record<string, unknown>> | undefined, opts: LootOpts, rng: Rng): boolean {
  for (const c of conditions ?? []) {
    const cond = c.condition ?? c.type;
    if (cond === 'killed_by_player' && !opts.killedByPlayer) return false;
    if (cond === 'random_chance') {
      const chance = (c.chance as number) ?? 1;
      if (rng.nextFloat() >= chance) return false;
    }
  }
  return true;
}

function pickEntry(entries: LootEntry[], rng: Rng): LootEntry | null {
  let total = 0;
  for (const e of entries) total += e.weight ?? 1;
  if (total === 0) return null;
  let r = rng.nextFloat() * total;
  for (const e of entries) { r -= e.weight ?? 1; if (r < 0) return e; }
  return entries[entries.length - 1] ?? null;
}

/** Roll one loot table into a list of item stacks. */
export function rollLootTable(reg: Registries, table: LootTable, rng: Rng, opts: LootOpts = {}): ItemStack[] {
  const out: ItemStack[] = [];
  for (const pool of table.pools) {
    const rolls = pool.rolls ?? 1;
    for (let r = 0; r < rolls; r++) {
      const entry = pickEntry(pool.entries as LootEntry[], rng);
      if (!entry || entry.type !== 'item' || !entry.name) continue;
      if (!conditionsPass(entry.conditions, opts, rng)) continue;
      const id = reg.items.idOf.get(entry.name);
      if (id === undefined) continue;
      let count = 1;
      let replacement: ItemStack | null = null;
      for (const fn of entry.functions ?? []) {
        const f = fn.function ?? fn.type;
        if (f === 'explorer_map') {
          // A treasure/explorer map: swap the plain `map` for one already pinned to
          // the nearest structure of `destination` (host-resolved).
          replacement = opts.explorerMap?.(String(fn.destination ?? '')) ?? null;
        } else if (f === 'set_count') {
          const min = (fn.min as number) ?? 1;
          const max = (fn.max as number) ?? min;
          count = min + rng.nextInt(Math.max(1, max - min + 1));
        } else if (f === 'looting_add' && opts.looting) {
          count += rng.nextInt(opts.looting + 1);
        }
      }
      if (replacement) out.push(replacement);
      else if (count > 0) out.push(makeStack(id, count));
    }
  }
  return out;
}

/** Convenience: roll a table by name (registry lookup), [] if missing. */
export function rollLootByName(reg: Registries, name: string, rng: Rng, opts: LootOpts = {}): ItemStack[] {
  const table = reg.loot.get(name);
  return table ? rollLootTable(reg, table, rng, opts) : [];
}
